import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { 
  getPasswordProtectionEnabled, 
  setPasswordProtectionEnabled,
  getSiteMapConfig,
  setSiteMapConfig as saveSiteMapConfig,
  DEFAULT_SITE_MAP_VIEWBOX,
  type SiteMapConfig,
} from '@/lib/site-config';
import { trackEvent } from '@/lib/analytics';
import { Link } from 'react-router-dom';
import { Image as ImageIcon, ArrowRight, Map, RotateCcw } from 'lucide-react';
import { UnitLocationMap, invalidateSiteMapConfigCache } from '@/components/UnitLocationMap';

const AdminSettings = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Site Map Config state
  const [siteMapConfig, setSiteMapConfig] = useState<SiteMapConfig>({
    viewBox: DEFAULT_SITE_MAP_VIEWBOX,
  });
  const [siteMapSaving, setSiteMapSaving] = useState(false);
  const [siteMapMessage, setSiteMapMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const [isEnabled, mapConfig] = await Promise.all([
        getPasswordProtectionEnabled(),
        getSiteMapConfig(),
      ]);
      setEnabled(isEnabled);
      setSiteMapConfig(mapConfig);
    } catch (error) {
      console.error('Error loading config:', error);
      setMessage({ type: 'error', text: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (newValue: boolean) => {
    setSaving(true);
    setMessage(null);

    try {
      const success = await setPasswordProtectionEnabled(newValue);
      if (success) {
        setEnabled(newValue);
        setMessage({
          type: 'success',
          text: `Password protection ${newValue ? 'enabled' : 'disabled'}`,
        });
        trackEvent('vip_preview_cta_click', { action: 'admin_toggle', enabled: newValue });
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: 'Failed to update configuration' });
      }
    } catch (error) {
      console.error('Error updating config:', error);
      setMessage({ type: 'error', text: 'Failed to update configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSiteMapConfig = async () => {
    setSiteMapSaving(true);
    setSiteMapMessage(null);

    try {
      const success = await saveSiteMapConfig(siteMapConfig);
      if (success) {
        // Invalidate the cached config so all UnitLocationMap instances reload
        invalidateSiteMapConfigCache();
        
        setSiteMapMessage({
          type: 'success',
          text: 'Site map settings saved successfully',
        });
        trackEvent('site_map_config_update', { 
          viewBox: siteMapConfig.viewBox,
          centerOffset: siteMapConfig.centerOffset,
          hasBackgroundImage: !!siteMapConfig.backgroundImage,
        });
        
        // Clear message after 3 seconds
        setTimeout(() => setSiteMapMessage(null), 3000);
      } else {
        setSiteMapMessage({ type: 'error', text: 'Failed to save site map settings' });
      }
    } catch (error) {
      console.error('Error saving site map config:', error);
      setSiteMapMessage({ type: 'error', text: 'Failed to save site map settings' });
    } finally {
      setSiteMapSaving(false);
    }
  };

  const handleResetSiteMapConfig = () => {
    setSiteMapConfig({
      viewBox: DEFAULT_SITE_MAP_VIEWBOX,
      centerOffset: undefined,
      backgroundImage: undefined,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Site Settings</h2>
        <p className="text-muted-foreground">
          Configure site-wide settings and preferences
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Password Protection</CardTitle>
          <CardDescription>
            Enable or disable the VIP preview password gate on the homepage. When enabled, visitors must enter a passcode to view the full site.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <label htmlFor="password-protection" className="text-sm font-medium">
                Presales Password Protection
              </label>
              <p className="text-sm text-muted-foreground">
                {enabled
                  ? 'Password protection is currently active'
                  : 'Password protection is currently disabled'}
              </p>
            </div>
            <Switch
              id="password-protection"
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={saving}
            />
          </div>

          {message && (
            <div
              className={`mt-4 p-3 rounded-md ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-800'
                  : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-800'
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> The password is configured via the{' '}
              <code className="bg-muted px-1 py-0.5 rounded">VITE_PRESALES_PASSWORD</code>{' '}
              environment variable. Changes to this toggle take effect immediately.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Image Management Link */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Image Management
          </CardTitle>
          <CardDescription>
            Manage hero images, stock photos, and amenity images. All images are automatically optimized with responsive variants.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/admin/images">
            <Button variant="outline" className="w-full sm:w-auto">
              Manage Images
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Site Map Display Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Map className="h-5 w-5" />
            Site Map Display
          </CardTitle>
          <CardDescription>
            Configure how unit location maps are displayed across the site. Adjust centering to compensate for labels or empty space in the source image.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* ViewBox Input */}
          <div className="space-y-2">
            <Label htmlFor="viewbox">ViewBox (x y width height)</Label>
            <Input
              id="viewbox"
              value={siteMapConfig.viewBox}
              onChange={(e) => setSiteMapConfig({ ...siteMapConfig, viewBox: e.target.value })}
              placeholder="0 0 1500 864"
            />
            <p className="text-xs text-muted-foreground">
              Default: "{DEFAULT_SITE_MAP_VIEWBOX}". Reduce width to crop from right side, adjust x to shift horizontally.
            </p>
          </div>

          {/* Center Offset Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Center Offset</Label>
              <span className="text-sm text-muted-foreground">
                {siteMapConfig.centerOffset || 0}px
              </span>
            </div>
            <Slider
              value={[siteMapConfig.centerOffset || 0]}
              onValueChange={([v]) => setSiteMapConfig({ ...siteMapConfig, centerOffset: v === 0 ? undefined : v })}
              min={-300}
              max={300}
              step={10}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>-300px (left)</span>
              <span>0</span>
              <span>+300px (right)</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Fine-tune horizontal centering. Overrides viewBox x-offset when set.
            </p>
          </div>

          {/* Background Image URL */}
          <div className="space-y-2">
            <Label htmlFor="bg-image">Background Image URL (optional)</Label>
            <Input
              id="bg-image"
              value={siteMapConfig.backgroundImage || ''}
              onChange={(e) => setSiteMapConfig({ ...siteMapConfig, backgroundImage: e.target.value || undefined })}
              placeholder="/img/site-plan-overlay.png"
            />
            <p className="text-xs text-muted-foreground">
              Optional overlay image for parking, shopping areas, or wide Grand Harbour view.
            </p>
          </div>

          {/* Live Preview */}
          <div className="space-y-2">
            <Label>Live Preview</Label>
            <div className="border rounded-lg p-4 bg-muted/30">
              <UnitLocationMap
                unitNumber="A-101"
                viewBox={siteMapConfig.viewBox}
                centerOffset={siteMapConfig.centerOffset}
                backgroundImage={siteMapConfig.backgroundImage}
                showTitle={false}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Showing unit A-101 as reference. Changes apply to all unit location maps site-wide.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleSaveSiteMapConfig} disabled={siteMapSaving}>
              {siteMapSaving ? 'Saving...' : 'Save Site Map Settings'}
            </Button>
            <Button variant="outline" onClick={handleResetSiteMapConfig}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>

          {siteMapMessage && (
            <div
              className={`p-3 rounded-md ${
                siteMapMessage.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-800'
                  : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-800'
              }`}
            >
              {siteMapMessage.text}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
